"use client";

import { EnvelopeIcon } from "@heroicons/react/24/outline";

import { useLanguage } from "../context/LanguageContext";
import { CONSENT_OPEN_EVENT } from "../lib/consent";

function LinkedInIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      focusable="false"
      fill="currentColor"
    >
      <path d="M20.447 20.452H16.89v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.345V9h3.414v1.561h.049c.476-.9 1.637-1.85 3.37-1.85 3.602 0 4.268 2.37 4.268 5.455v6.286zM5.337 7.433a1.987 1.987 0 1 1 0-3.974 1.987 1.987 0 0 1 0 3.974zM7.119 20.452H3.555V9h3.564v11.452z" />
    </svg>
  );
}

export default function SiteFooter(): JSX.Element {
  const { t, locale } = useLanguage();
  const accessibilityHref = `/${locale}/accessibility`;
  const privacyHref = `/${locale}/privacy`;
  const termsHref = `/${locale}/terms`;

  const handleCookiePreferences = (): void => {
    if (typeof window === "undefined") {
      return;
    }
    window.dispatchEvent(new Event(CONSENT_OPEN_EVENT));
  };

  return (
    <footer
      className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
      data-testid="site-footer"
    >
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Row 1: Primary content */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-base text-slate-900 dark:text-slate-100">
            {t.common.footer.copyright}
          </p>
          <div className="flex flex-wrap items-center gap-3 text-base text-slate-700 dark:text-slate-300">
            <a
              href={`mailto:${t.common.footer.email}`}
              aria-label={t.common.footer.contactAriaLabel}
              data-testid="footer-email"
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 no-underline hover:text-slate-900 dark:hover:text-slate-100 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900"
            >
              <EnvelopeIcon className="h-5 w-5" aria-hidden="true" />
              <span>{t.common.nav.contact}</span>
            </a>
            <a
              href="https://www.linkedin.com/in/merukimoon"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t.common.footer.linkedinAriaLabel}
              data-testid="footer-linkedin"
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 no-underline hover:text-slate-900 dark:hover:text-slate-100 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900"
            >
              <LinkedInIcon className="h-5 w-5" />
              <span>{t.common.footer.linkedin}</span>
            </a>
          </div>
        </div>

        {/* Row 2: Secondary content */}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <a
              href={accessibilityHref}
              className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            >
              {t.common.footer.accessibility}
            </a>
            <span className="text-slate-400 dark:text-slate-500">·</span>
            <a
              href={privacyHref}
              className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            >
              {t.common.footer.privacy}
            </a>
            <span className="text-slate-400 dark:text-slate-500">·</span>
            <a
              href={termsHref}
              className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            >
              {t.common.footer.terms}
            </a>
            <span className="text-slate-400 dark:text-slate-500">·</span>
            <button
              type="button"
              onClick={handleCookiePreferences}
              className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors text-left"
            >
              {t.common.footer.cookiePreferences}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
